import React, { useState, useEffect } from 'react';
import apiService, { errorHelpers, formatHelpers, authHelpers } from '../../../utils/apiService';

export function CertificateModal({ cert, onClose, onSuccess }) {
    const isEdit = !!cert;
    const [form, setForm] = useState({
        certificate_number: cert?.certificate_number || '',
        student_name: cert?.student_name || '',
        matric_number: cert?.matric_number || '',
        course: cert?.course || '',
        graduation_date: cert?.graduation_date || '',
        grade: cert?.grade || '',
        department: cert?.department || '',
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const handleChange = e => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});
        try {
            if (isEdit) {
                await apiService.admin.updateCertificate(cert.id, form);
            } else {
                await apiService.admin.createCertificate(form);
            }
            onSuccess();
        } catch (err) {
            const msg = errorHelpers.getErrorMessage(err);
            setErrors({ submit: msg });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal">
                <header className="modal-header">
                    <h3>{isEdit ? 'Edit' : 'Add'} Certificate</h3>
                    <button className="close-btn" onClick={onClose}>×</button>
                </header>
                <form onSubmit={handleSubmit} className="modal-body">
                    {[
                        { k: 'certificate_number', t: 'Certificate #', req: true },
                        { k: 'student_name', t: 'Student Name', req: true },
                        { k: 'matric_number', t: 'Matric #' },
                        { k: 'course', t: 'Course', req: true },
                        { k: 'graduation_date', t: 'Graduation Date', req: true },
                        { k: 'grade', t: 'Grade', req: true },
                        { k: 'department', t: 'Department', req: true },
                    ].map(({ k, t, req }) => (
                        <div className="form-group" key={k}>
                            <label>{t}{req && '*'}</label>
                            <input
                                type="text"
                                name={k}
                                value={form[k]}
                                onChange={handleChange}
                                required={!!req}
                            />
                        </div>
                    ))}

                    {errors.submit && (
                        <div className="form-error">{errors.submit}</div>
                    )}

                    <footer className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                            disabled={submitting}
                        >Cancel</button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={submitting}
                        >
                            {submitting ? (isEdit ? 'Saving…' : 'Creating…')
                                : (isEdit ? 'Save Changes' : 'Create')}
                        </button>
                    </footer>
                </form>
            </div>
        </div>
    );
}


const CertificateManagement = () => {
    const [certs, setCerts] = useState([]);
    const [pagination, setPg] = useState({});
    const [filters, setFilters] = useState({ search: '' });
    const [loading, setLoading] = useState(true);

    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [editCert, setEditCert] = useState(null);

    // Reload list
    const loadCerts = async (page = 1) => {
        setLoading(true);
        try {
            const { data } = await apiService.admin.getCertificates({
                ...filters, page, per_page: 20
            });
            setCerts(data.certificates);
            setPg(data.pagination);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadCerts() }, [filters]);

    const handleFilterChange = (k, v) => {
        setFilters(f => ({ ...f, [k]: v }));
    };

    // Handlers
    const onCreateSuccess = () => {
        setShowCreate(false);
        loadCerts();
    };
    const onEdit = (cert) => {
        setEditCert(cert);
        setShowEdit(true);
    };
    const onEditSuccess = () => {
        setShowEdit(false);
        loadCerts();
    };
    const onDelete = async (id) => {
        if (!window.confirm('Delete this certificate?')) return;
        try {
            await apiService.admin.deleteCertificate(id);
            loadCerts();
        } catch (err) {
            alert(errorHelpers.getErrorMessage(err));
        }
    };

    return (
        <div className="certificate-management">
            <div className="management-header">
                <h2>📜 Certificate Management</h2>
                <button
                    className="btn btn-primary"
                    onClick={() => setShowCreate(true)}
                >
                    + Add Certificate
                </button>
            </div>

            <div className="filters-section">
                <input
                    type="text"
                    className="filter-input"
                    placeholder="Search by cert#, name or course…"
                    value={filters.search}
                    onChange={e => handleFilterChange('search', e.target.value)}
                />
            </div>

            {loading
                ? <p>Loading…</p>
                : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Cert&nbsp;#</th>
                                <th>Name</th>
                                <th>Matric&nbsp;#</th>
                                <th>Course</th>
                                <th>Grad&nbsp;Date</th>
                                <th>Grade</th>
                                <th>Dept</th>
                                <th>Uploaded&nbsp;By</th>
                                <th>At</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {certs.map(c => (
                                <tr key={c.id}>
                                    <td>{c.certificate_number}</td>
                                    <td>{c.student_name}</td>
                                    <td>{c.matric_number}</td>
                                    <td>{c.course}</td>
                                    <td>{c.graduation_date}</td>
                                    <td>{c.grade || '—'}</td>
                                    <td>{c.department || '—'}</td>
                                    <td>{c.uploader_email || '—'}</td>
                                    <td>{new Date(c.uploaded_at).toLocaleString()}</td>
                                    <td>
                                        <button onClick={() => onEdit(c)}>✏️</button>
                                        <button onClick={() => onDelete(c.id)}>🗑️</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )
            }

            {/* Pagination */}
            <div className="pagination">
                <button
                    disabled={!pagination.has_prev}
                    onClick={() => loadCerts(pagination.page - 1)}
                >Prev</button>
                <span>Page {pagination.page} of {pagination.pages}</span>
                <button
                    disabled={!pagination.has_next}
                    onClick={() => loadCerts(pagination.page + 1)}
                >Next</button>
            </div>

            {/* Modals */}
            {showCreate && (
                <CertificateModal
                    onClose={() => setShowCreate(false)}
                    onSuccess={onCreateSuccess}
                />
            )}
            {showEdit && (
                <CertificateModal
                    cert={editCert}
                    onClose={() => setShowEdit(false)}
                    onSuccess={onEditSuccess}
                />
            )}

            <style jsx>{`
        .user-management {
          max-width: 1200px;
        }

        .management-header {
          margin-bottom: 2rem;
        }

        .management-header h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--gray-800);
          margin-bottom: 0.5rem;
        }

        .management-header p {
          color: var(--gray-600);
        }

        .filters-section {
          background: white;
          padding: 1.5rem;
          border-radius: 0.75rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
        }

        .filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
        }

        .filter-label {
          font-weight: 500;
          color: var(--gray-700);
          margin-bottom: 0.5rem;
        }

        .filter-input,
        .filter-select {
          padding: 0.75rem;
          border: 2px solid var(--gray-200);
          border-radius: 0.5rem;
          font-size: 1rem;
          transition: border-color 0.3s ease;
        }

        .filter-input:focus,
        .filter-select:focus {
          outline: none;
          border-color: var(--secondary-blue);
        }

        .table-container {
          background: white;
          border-radius: 0.75rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .loading-state,
        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid var(--gray-200);
          border-top: 4px solid var(--secondary-blue);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
        }

        .empty-state h3 {
          color: var(--gray-700);
          margin-bottom: 0.5rem;
        }

        .empty-state p {
          color: var(--gray-600);
        }

        .users-table-wrapper {
          overflow-x: auto;
        }

        .users-table {
          width: 100%;
          border-collapse: collapse;
        }

        .users-table th,
        .users-table td {
          padding: 1rem;
          text-align: left;
          border-bottom: 1px solid var(--gray-200);
        }

        .users-table th {
          background: var(--gray-50);
          font-weight: 600;
          color: var(--gray-700);
          position: sticky;
          top: 0;
        }

        .users-table tr:hover {
          background: var(--gray-50);
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .user-avatar {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .user-details {
          flex: 1;
        }

        .user-name {
          font-weight: 500;
          color: var(--gray-800);
        }

        .user-email {
          font-size: 0.875rem;
          color: var(--gray-600);
        }

        .role-select {
          background: none;
          border: none;
          font-weight: 500;
          font-size: 0.875rem;
          padding: 0.25rem 0.5rem;
          border-radius: 1rem;
          cursor: pointer;
        }

        .role-select.badge-error {
          background: var(--red-100);
          color: var(--error-red);
        }

        .role-select.badge-warning {
          background: var(--yellow-100);
          color: var(--warning-yellow);
        }

        .role-select.badge-info {
          background: var(--blue-100);
          color: var(--secondary-blue);
        }

        .status-badge {
          font-size: 0.875rem;
          font-weight: 500;
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
        }

        .status-badge.enabled {
          background: var(--green-100);
          color: var(--success-green);
        }

        .status-badge.disabled {
          background: var(--red-100);
          color: var(--error-red);
        }

        .verification-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .verification-badge {
          font-size: 0.875rem;
          font-weight: 500;
        }

        .verification-badge.verified {
          color: var(--success-green);
        }

        .verification-badge.unverified {
          color: var(--warning-yellow);
        }

        .verify-btn {
          background: var(--blue-100);
          color: var(--secondary-blue);
          border: none;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.75rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .verify-btn:hover {
          background: var(--secondary-blue);
          color: white;
        }

        .join-date {
          font-size: 0.875rem;
          color: var(--gray-600);
        }

        .actions {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          border: none;
          padding: 0.5rem 0.75rem;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .action-btn.disable {
          background: var(--red-100);
          color: var(--error-red);
        }

        .action-btn.disable:hover {
          background: var(--error-red);
          color: white;
        }

        .action-btn.enable {
          background: var(--green-100);
          color: var(--success-green);
        }

        .action-btn.enable:hover {
          background: var(--success-green);
          color: white;
        }

        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          margin-top: 2rem;
          padding: 2rem 0;
        }

        .pagination-btn {
          background: white;
          border: 2px solid var(--gray-300);
          color: var(--gray-700);
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .pagination-btn:hover:not(:disabled) {
          border-color: var(--secondary-blue);
          color: var(--secondary-blue);
        }

        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-info {
          font-weight: 500;
          color: var(--gray-700);
          text-align: center;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .filters-grid {
            grid-template-columns: 1fr;
          }

          .users-table {
            font-size: 0.875rem;
          }

          .users-table th,
          .users-table td {
            padding: 0.75rem 0.5rem;
          }

          .user-info {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .pagination {
            flex-direction: column;
            gap: 0.5rem;
          }

          .pagination-btn {
            width: 100%;
            max-width: 200px;
          }
        }

        /* Modal Styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }
                .modal {
                    background: white;
                    border-radius: 0.75rem;
                    width: 90%;
                    max-width: 500px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    overflow: hidden;
                }
                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem;
                    border-bottom: 1px solid #e5e5e5;
                }
                .close-btn {
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    cursor: pointer;
                }
                .modal-body {
                    padding: 1rem;
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }
                .modal-body input,
                .modal-body textarea,
                .modal-body select {
                    padding: 0.5rem;
                    border: 1px solid #ccc;
                    border-radius: 0.5rem;
                    width: 100%;
                }
                .modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 0.5rem;
                    padding: 1rem;
                    border-top: 1px solid #e5e5e5;
                }
                .btn {
                    padding: 0.5rem 1rem;
                    border-radius: 0.5rem;
                    font-weight: 500;
                    cursor: pointer;
                }
                .btn.cancel {
                    background: #f3f3f3;
                    color: #333;
                }
                .btn.save {
                    background: var(--secondary-blue);
                    color: white;
                }
                .action-btn.edit {
                    background: var(--blue-100);
                    color: var(--secondary-blue);
                }
                .action-btn.edit:hover {
                    background: var(--secondary-blue);
                    color: white;
                }

                .modal-backdrop {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
  .verification-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
.button-spinner {
  width: 16px;
  height: 16px;
  vertical-align: middle;
}
.resend-btn[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}


      `}</style>
        </div>
    );
};

export default CertificateManagement;
